import React, { useEffect, useRef } from 'react';
import { ChevronDown, Eye, EyeOff, TrendingUp, Clock, CalendarDays, Receipt, Wallet } from 'lucide-react';

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatEuro(num) {
  const fixed = Math.abs(num).toFixed(2);
  const [whole, dec] = fixed.split('.');
  const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${num >= 0 ? '+' : '-'}${formattedWhole},${dec} €`;
}

export function formatEuroShort(num) {
  const value = Number(num) || 0;
  const fixed = Math.abs(value).toFixed(2);
  const [whole, dec] = fixed.split('.');
  const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formattedWhole},${dec}€`;
}

export function ChartCanvas({ chartH, chartW, dailyData, month, selectedDay }) {
  const canvasRef = useRef(null);
  const selIdx = Math.max(0, Math.min(selectedDay - 1, dailyData.length - 1));
  const selData = dailyData[selIdx] || { day: selectedDay, profit: 0, qr_count: 0 };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !dailyData.length) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = chartW;
    const cssH = chartH;
    if (canvas.width !== cssW * dpr || canvas.height !== cssH * dpr) {
      canvas.width = cssW * dpr;
      canvas.height = cssH * dpr;
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    const N = dailyData.length;
    const padX = 14;
    const padTop = 34;
    const padBot = 14;
    const usableW = cssW - padX * 2;
    const usableH = cssH - padTop - padBot;
    const gap = Math.max(2, Math.min(6, (usableW / N) * 0.28));
    const barW = Math.max(3, (usableW - gap * (N - 1)) / N);
    // Plot transaction ACTIVITY (qr_count) so busy days grow UP, not down.
    // Profit stays in the header/tooltip because on this business model a
    // per-transaction "profit" is often negative (loyalty payout) and plotting
    // it would spike the line downwards on the venue's best days.
    const counts = dailyData.map((day) => Math.max(0, Number(day.qr_count) || 0));
    const maxC = Math.max(1, ...counts);
    const baselineY = cssH - padBot;

    // Two faint horizontal guide lines (25% and 75%) for a subtle grid
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.05)';
    ctx.lineWidth = 1;
    [0.25, 0.5, 0.75].forEach((frac) => {
      const y = baselineY - usableH * frac + 0.5;
      ctx.beginPath();
      ctx.moveTo(padX, y);
      ctx.lineTo(cssW - padX, y);
      ctx.stroke();
    });

    // Baseline (slightly stronger)
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)';
    ctx.beginPath();
    ctx.moveTo(padX, baselineY + 0.5);
    ctx.lineTo(cssW - padX, baselineY + 0.5);
    ctx.stroke();

    // Bars
    counts.forEach((c, i) => {
      const cx = padX + i * (barW + gap);
      const isSel = i === selIdx;
      const h = c > 0 ? Math.max(6, (c / maxC) * usableH) : 3;
      const y = baselineY - h;
      const r = Math.min(barW / 2, 4);

      // Bar path (rounded top corners only)
      const drawBarPath = () => {
        ctx.beginPath();
        ctx.moveTo(cx, baselineY);
        ctx.lineTo(cx, y + r);
        ctx.quadraticCurveTo(cx, y, cx + r, y);
        ctx.lineTo(cx + barW - r, y);
        ctx.quadraticCurveTo(cx + barW, y, cx + barW, y + r);
        ctx.lineTo(cx + barW, baselineY);
        ctx.closePath();
      };

      const grad = ctx.createLinearGradient(0, y, 0, baselineY);
      if (c === 0) {
        // Empty day - tiny neutral pill so the axis reads as populated
        grad.addColorStop(0, 'rgba(150, 150, 165, 0.45)');
        grad.addColorStop(1, 'rgba(150, 150, 165, 0.18)');
      } else if (isSel) {
        grad.addColorStop(0, '#FF9563');
        grad.addColorStop(1, '#FF5316');
      } else {
        grad.addColorStop(0, '#FFC7A6');
        grad.addColorStop(1, '#FF8A47');
      }

      if (isSel && c > 0) {
        ctx.shadowColor = 'rgba(255, 122, 42, 0.35)';
        ctx.shadowBlur = 10;
      }
      ctx.fillStyle = grad;
      drawBarPath();
      ctx.fill();
      ctx.shadowBlur = 0;
    });

    // Selection guide line + dot above selected bar
    const selBarCx = padX + selIdx * (barW + gap) + barW / 2;
    const selCount = counts[selIdx];
    const selBarH = selCount > 0 ? Math.max(6, (selCount / maxC) * usableH) : 3;
    const selBarTopY = baselineY - selBarH;

    ctx.beginPath();
    ctx.setLineDash([3, 4]);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.22)';
    ctx.lineWidth = 1;
    ctx.moveTo(selBarCx, padTop);
    ctx.lineTo(selBarCx, selBarTopY - 6);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.arc(selBarCx, selBarTopY - 6, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    ctx.strokeStyle = '#FF5316';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [dailyData, selIdx, chartW, chartH]);

  const profitColor = selData.profit === 0
    ? 'var(--text-2)'
    : (selData.profit > 0 ? 'var(--status-success-text)' : '#B84A2A');

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: chartH, borderRadius: 12 }}
      />
      <div
        style={{
          position: 'absolute',
          left: 10,
          top: 8,
          fontFamily: "'Inria Serif', serif",
          fontSize: 10,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: 'var(--text-3)',
          fontWeight: 700,
        }}
      >
        Daily transactions
      </div>
      <div
        style={{
          position: 'absolute',
          right: 10,
          top: 4,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--surface)',
          border: '1px solid var(--card-border)',
          borderRadius: 999,
          padding: '4px 10px',
          fontFamily: "'Inria Serif', serif",
          fontSize: 12,
          color: 'var(--text)',
          boxShadow: 'var(--premium-shadow-nested)',
        }}
        data-testid="venue-analytics-chart-selection-chip"
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: 'linear-gradient(180deg, #FF9563, #FF5316)',
            display: 'inline-block',
          }}
        />
        <span style={{ fontWeight: 700 }}>{selData.qr_count} tx</span>
        <span style={{ color: 'var(--text-3)' }}>·</span>
        <span style={{ color: profitColor, fontWeight: 700 }}>{formatEuroShort(selData.profit)}</span>
      </div>
      <div
        style={{
          textAlign: 'center',
          marginTop: 6,
          fontFamily: "'Inria Serif', serif",
          fontSize: 13,
          color: 'var(--text-2)',
        }}
        data-testid="venue-analytics-chart-selected-day"
      >
        {selData.label ? selData.label : `${selData.day} ${MONTHS[month]}`}
      </div>
    </div>
  );
}

export const VenueAnalyticsControls = ({
  data,
  month,
  setMonth,
  setShowMonthPicker,
  setShowYearPicker,
  setYear,
  showMonthPicker,
  showYearPicker,
  year,
  rangeMode,
  setRangeMode,
}) => (
  <div style={{ width: '100%', maxWidth: 360, padding: '0 10px', marginTop: 20 }}>
    {/* Segmented toggle: Last 30 days vs Monthly */}
    <div
      role="tablist"
      style={{
        display: 'flex',
        background: 'var(--surface-2)',
        border: '1px solid var(--card-border)',
        borderRadius: 12,
        padding: 4,
        gap: 4,
      }}
    >
      {[
        { value: 'range', label: 'Last 30 days', testId: 'venue-analytics-range-toggle-30d' },
        { value: 'month', label: 'Monthly', testId: 'venue-analytics-range-toggle-month' },
      ].map((opt) => {
        const active = rangeMode === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => setRangeMode(opt.value)}
            data-testid={opt.testId}
            style={{
              flex: 1,
              padding: '8px 12px',
              border: 'none',
              borderRadius: 9,
              cursor: 'pointer',
              fontFamily: "'Inria Serif', serif",
              fontSize: 14,
              fontWeight: 700,
              background: active ? 'var(--surface)' : 'transparent',
              color: active ? 'var(--accent-purple)' : 'var(--text-3)',
              boxShadow: active ? 'var(--premium-shadow-nested)' : 'none',
              transition: 'background 160ms ease, color 160ms ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>

    {rangeMode === 'month' ? (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
        <div style={{ position: 'relative' }}>
          <button onClick={() => { setShowYearPicker(!showYearPicker); setShowMonthPicker(false); }} data-testid="venue-analytics-year-btn" style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--text)', background: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 14, padding: '8px 14px', cursor: 'pointer', boxShadow: 'var(--premium-shadow-nested)' }}>
            {year} <ChevronDown size={16} />
          </button>
          {showYearPicker ? (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 20, background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--card-border)', boxShadow: 'var(--shadow-2)', marginTop: 6, overflow: 'hidden' }}>
              {[2024, 2025, 2026, 2027].map((pickerYear) => (
                <button key={pickerYear} onClick={() => { setYear(pickerYear); setShowYearPicker(false); }} style={{ display: 'block', width: '100%', padding: '8px 20px', fontFamily: "'Inria Serif', serif", fontSize: 16, border: 'none', background: pickerYear === year ? 'var(--accent-surface)' : 'transparent', cursor: 'pointer', textAlign: 'left', color: pickerYear === year ? 'var(--accent-purple)' : 'var(--text)' }}>{pickerYear}</button>
              ))}
            </div>
          ) : null}
        </div>

        <div style={{ position: 'relative' }}>
          <button onClick={() => { setShowMonthPicker(!showMonthPicker); setShowYearPicker(false); }} data-testid="venue-analytics-month-btn" style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 400, color: 'var(--text)', background: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 14, padding: '8px 14px', cursor: 'pointer', boxShadow: 'var(--premium-shadow-nested)' }}>
            <ChevronDown size={16} /> {MONTHS[month]}
          </button>
          {showMonthPicker ? (
            <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 20, background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--card-border)', boxShadow: 'var(--shadow-2)', marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
              {MONTHS.map((pickerMonth, index) => (
                <button key={pickerMonth} onClick={() => { setMonth(index); setShowMonthPicker(false); }} style={{ display: 'block', width: '100%', padding: '8px 20px', fontFamily: "'Inria Serif', serif", fontSize: 16, border: 'none', background: index === month ? 'var(--accent-surface)' : 'transparent', cursor: 'pointer', textAlign: 'left', color: index === month ? 'var(--accent-purple)' : 'var(--text)' }}>{pickerMonth}</button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    ) : (
      <div style={{ marginTop: 10, fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text-3)', textAlign: 'center' }}>
        Rolling window — updated live
      </div>
    )}
  </div>
);

export const VenueAnalyticsChartCard = ({ chartH, chartW, dailyData, data, handleSliderDown, month, selectedDay, sliderRatio, sliderRef, onOpenWeekly }) => {
  const monthlyProfit = Number(data?.monthly_profit || 0);
  const prevMonthlyProfit = Number(data?.prev_monthly_profit || 0);
  const monthlyQrs = Number(data?.monthly_qrs || 0);
  const prevMonthlyQrs = Number(data?.prev_monthly_qrs || 0);
  const isRange = data?.mode === 'range';
  const rangeDays = Number(data?.range_days || 30);
  const periodLabel = isRange ? `Last ${rangeDays} days profit` : 'Monthly profit';

  const buildDelta = (current, prev) => {
    if (!prev || prev <= 0) return null;
    const pct = Math.round(((current - prev) / prev) * 100);
    // Per product: only celebrate growth — hide the pill entirely when the
    // venue is performing the same or worse than last month so we never
    // surface negative numbers to the venue owner.
    if (pct <= 0) return null;
    return {
      pct,
      Icon: TrendingUp,
      color: 'var(--status-success-text)',
      bg: 'rgba(0, 179, 65, 0.10)',
    };
  };

  const profitDelta = buildDelta(monthlyProfit, prevMonthlyProfit);
  const qrsDelta = buildDelta(monthlyQrs, prevMonthlyQrs);

  const renderDeltaPill = (delta, testId) => {
    if (!delta) return null;
    const { pct, Icon, color, bg } = delta;
    return (
      <span
        data-testid={testId}
        style={{
          marginTop: 4,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 9999,
          background: bg,
          color,
          fontFamily: "'Inria Serif', serif",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.1,
        }}
      >
        <Icon size={11} strokeWidth={2.5} />
        +{pct}% vs last
      </span>
    );
  };

  return (
    <div className="premium-card" data-testid="venue-analytics-chart-card" style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 16, borderRadius: 22, padding: '14px 16px 12px', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, paddingBottom: 6 }}>
        <button
          type="button"
          onClick={() => onOpenWeekly?.('profit')}
          data-testid="venue-analytics-profit-tap"
          style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: onOpenWeekly ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, textAlign: 'left' }}
        >
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 11, letterSpacing: 1.1, textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 700 }}>
            {periodLabel}
          </span>
          <span data-testid="venue-analytics-total-monthly" style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--status-success-text)', marginTop: 2, lineHeight: 1.05 }}>
            {formatEuro(monthlyProfit)}
          </span>
          {renderDeltaPill(profitDelta, 'venue-analytics-profit-delta')}
        </button>
        <button
          type="button"
          onClick={() => onOpenWeekly?.('transactions')}
          data-testid="venue-analytics-transactions-tap"
          style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: onOpenWeekly ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', textAlign: 'right' }}
        >
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 11, letterSpacing: 1.1, textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 700 }}>
            Transactions
          </span>
          <span data-testid="venue-analytics-total-transactions" style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--text)', marginTop: 2, lineHeight: 1.05 }}>
            {monthlyQrs.toLocaleString()}
          </span>
          {renderDeltaPill(qrsDelta, 'venue-analytics-qrs-delta')}
        </button>
      </div>
      <ChartCanvas dailyData={dailyData} chartW={chartW} chartH={chartH} selectedDay={selectedDay} month={month} />
      <div ref={sliderRef} onMouseDown={handleSliderDown} onTouchStart={handleSliderDown} data-testid="venue-analytics-slider" style={{ width: '100%', height: 30, position: 'relative', cursor: 'pointer', marginTop: 4, touchAction: 'none' }}>
        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 2, backgroundColor: 'var(--text-2)', transform: 'translateY(-50%)' }} />
        <div style={{ position: 'absolute', top: '50%', left: `${sliderRatio * 100}%`, width: 14, height: 14, borderRadius: '50%', backgroundColor: 'var(--accent-purple)', transform: 'translate(-50%, -50%)', boxShadow: 'var(--premium-shadow-main)' }} />
      </div>
    </div>
  );
};

export const VenueAnalyticsTotalProfit = ({ data, hideTotal, setHideTotal }) => {
  const monthly = Number(data?.monthly_profit || 0);
  const prev = Number(data?.prev_monthly_profit || 0);
  const deltaPct = prev > 0 ? ((monthly - prev) / prev) * 100 : 0;
  const deltaRounded = Math.round(deltaPct);
  // Per product: only show the MoM growth pill when growth is positive — never
  // surface "-10% vs last" or similar to the venue owner.
  const showDelta = prev > 0 && deltaRounded > 0;
  const totalProfit = Number(data?.total_profit || 0);
  const isRange = data?.mode === 'range';
  const rangeDays = Number(data?.range_days || 30);
  const deltaSuffix = isRange ? `this ${rangeDays}d vs prior ${rangeDays}d` : 'this month vs last';

  return (
    <div style={{ width: '100%', maxWidth: 360, padding: '0 16px', marginTop: 22 }}>
      <div
        data-testid="venue-analytics-alltime-card"
        className="premium-card"
        style={{
          width: '100%',
          padding: '16px 18px 18px',
          borderRadius: 22,
          position: 'relative',
          background: 'var(--surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              aria-hidden
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                background: 'var(--status-success-surface)',
                color: 'var(--status-success-text)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Wallet size={18} strokeWidth={2.2} />
            </span>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 700 }}>
                All-time profit
              </span>
              <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text-3)', marginTop: 2 }}>
                Since you joined Allure
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setHideTotal(!hideTotal)}
            data-testid="venue-analytics-hide-total"
            aria-label={hideTotal ? 'Show all-time profit' : 'Hide all-time profit'}
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--card-border)',
              borderRadius: 9999,
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--text-2)',
              fontFamily: "'Inria Serif', serif",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {hideTotal ? <Eye size={14} strokeWidth={2.2} /> : <EyeOff size={14} strokeWidth={2.2} />}
            {hideTotal ? 'Show' : 'Hide'}
          </button>
        </div>

        <div
          data-testid="venue-analytics-alltime-amount"
          style={{
            marginTop: 14,
            fontFamily: "'Inria Serif', serif",
            fontWeight: 700,
            fontSize: 32,
            lineHeight: 1.1,
            color: hideTotal ? 'var(--text-3)' : 'var(--status-success-text)',
            letterSpacing: hideTotal ? 4 : -0.5,
          }}
        >
          {hideTotal ? '••••••' : formatEuro(totalProfit)}
        </div>

        {showDelta ? (
          <div
            data-testid="venue-analytics-mom-delta"
            style={{
              marginTop: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 9999,
              background: 'rgba(0, 179, 65, 0.08)',
              color: 'var(--status-success-text)',
              fontFamily: "'Inria Serif', serif",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <TrendingUp size={13} strokeWidth={2.5} />
            +{deltaRounded}% {deltaSuffix}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const statTileStyle = {
  flex: 1,
  minWidth: 0,
  padding: '12px 12px 14px',
  background: 'var(--surface)',
  border: '1px solid var(--card-border)',
  borderRadius: 16,
  boxShadow: 'var(--premium-shadow-nested)',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const statIconWrapStyle = (bg, fg) => ({
  width: 28,
  height: 28,
  borderRadius: 8,
  background: bg,
  color: fg,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const VenueAnalyticsStatsTiles = ({ data }) => {
  const avgTicket = Number(data?.avg_ticket || 0);
  const peakHour = data?.peak_hour;
  const topDay = data?.top_day;

  const formatHour = (hour) => {
    if (hour == null) return '—';
    const end = (hour + 1) % 24;
    const pad = (num) => String(num).padStart(2, '0');
    return `${pad(hour)}:00 – ${pad(end)}:00`;
  };

  const formatAvg = (num) => {
    if (!num) return '—';
    return `€${num.toFixed(2).replace(/\.00$/, '')}`;
  };

  return (
    <div
      data-testid="venue-analytics-stats-tiles"
      style={{
        width: '100%',
        maxWidth: 360,
        padding: '0 16px',
        marginTop: 18,
        display: 'flex',
        gap: 10,
      }}
    >
      <div data-testid="venue-analytics-avg-ticket" style={statTileStyle}>
        <span style={statIconWrapStyle('rgba(0, 179, 65, 0.10)', 'var(--status-success-text)')}>
          <Receipt size={16} strokeWidth={2} />
        </span>
        <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Avg ticket</span>
        <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{formatAvg(avgTicket)}</span>
      </div>

      <div data-testid="venue-analytics-peak-hour" style={statTileStyle}>
        <span style={statIconWrapStyle('var(--accent-surface-soft)', 'var(--accent-purple)')}>
          <Clock size={16} strokeWidth={2} />
        </span>
        <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Peak hour</span>
        <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{formatHour(peakHour)}</span>
      </div>

      <div data-testid="venue-analytics-top-day" style={statTileStyle}>
        <span style={statIconWrapStyle('rgba(229, 57, 53, 0.10)', '#E53935')}>
          <CalendarDays size={16} strokeWidth={2} />
        </span>
        <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Top day</span>
        <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{topDay || '—'}</span>
      </div>
    </div>
  );
};